<template>
  <div class="c-widget-sortable-tabs-bar">
    <div v-resize:throttle.150="onResize">
      <div class="c-widget-sortable-tabs-bar__scrollbar-nav">
        <div class="c-widget-sortable-tabs-bar__nav-btn-left drag--exclude">
          <v-button v-if="scrollContentActive" @click="onStepLeft">
            <i class="fas fa-arrow-left" />
          </v-button>
        </div>
        <div class="c-widget-sortable-tabs-bar__nav-scroll-content" :style="{ justifyContent: scrollAlign.dir }">
          <vue-draggable
            ref="dragarea"
            v-model="tabsModel"
            :style="{
              marginLeft: scrollPosition.left + 'px',
              marginRight: scrollPosition.right + 'px'
            }"
            class="c-widget-sortable-tabs-bar__dragarea drag--exclude"
            v-bind="draggableOptions"
            @end="onTabDragEnd"
          >
            <div
              class="c-widget-sortable-tabs-bar__drag-tab"
              v-for="tab in tabsModel"
              :key="tab.id"
              :class="{ active: tab.active }"
            >
              <div class="c-widget-sortable-tabs-bar__drag-tab-label" :class="{ editable: tab.editable }">
                <div
                  v-if="!tab.editable"
                  @click="loadComponentsByTab(tab)"
                  @contextmenu.prevent="showEditDropDown($event, tab)"
                >
                  {{ tab.name }}
                </div>
                <div v-else>
                  <v-form-input
                    autofocus
                    class="c-widget-sortable-tabs-bar__drag-tab-input"
                    :class="{ error: formValidate.minLen }"
                    :maxlength="$appConfig.generalInputMaxLength"
                    :value="tab.name"
                    @blur="onTabViewChange(tab)"
                    @input="onInputChange($event, tab)"
                    @keyup.enter="onTabViewChange(tab)"
                  ></v-form-input>
                </div>
              </div>
            </div>
            <v-button class="c-widget-sortable-tabs-bar__btn-add-tab" @click.prevent.stop="showAddDropDown($event)">
              <i class="fas fa-plus" />
            </v-button>
          </vue-draggable>
        </div>
        <div class="c-widget-sortable-tabs-bar__nav-btn-right drag--exclude">
          <v-button v-if="scrollContentActive" @click="onStepRight">
            <i class="fas fa-arrow-right" />
          </v-button>
        </div>
      </div>
    </div>
    <v-dropdown
      class="c-widget-sortable-tabs-bar__add-dropdown drag--exclude"
      ref="add-dropdown"
      v-if="addDropDownActive"
      :style="{ top: dropDownPosition.top + 'px', left: dropDownPosition.left + 'px' }"
    >
      <v-dropdown-item
        href="#"
        v-for="(tabClass, index) in tabClasses"
        :key="index"
        @click.prevent.stop="onTabAdd(tabClass)"
        >{{ tabClass.name }}</v-dropdown-item
      >
    </v-dropdown>

    <v-dropdown
      class="c-widget-sortable-tabs-bar__edit-dropdown drag--exclude"
      ref="edit-dropdown"
      v-if="editDropDownActive"
      :style="{ top: dropDownPosition.top + 'px', left: dropDownPosition.left + 'px' }"
    >
      <v-dropdown-item href="#" @click="onTabRemove">{{ $t(`${i18nNamespace}.headerLabels.remove`) }}</v-dropdown-item>
      <v-dropdown-item href="#" @click="onTabRename">{{ $t(`${i18nNamespace}.headerLabels.rename`) }}</v-dropdown-item>
    </v-dropdown>
  </div>
</template>

<script>
import VueDraggable from 'vuedraggable';
import resize from 'vue-resize-directive';

export default {
  name: 'c-widget-sortable-tabs-bar',
  components: { VueDraggable },
  props: {
    tabs: {
      required: true,
      type: Array
    },
    tabClasses: {
      required: true,
      type: Array
    }
  },
  directives: {
    resize
  },
  computed: {
    i18nNamespace() {
      return 'white-label.widgets.accountDetails';
    }
  },
  watch: {
    tabs: {
      immediate: true,
      handler(tabs) {
        this.tabsModel = this.reorderByIndex(tabs);
      }
    }
  },
  data() {
    return {
      currentTab: null,
      dragAreaElement: null,
      addDropDownActive: false,
      editDropDownActive: false,
      scrollContentActive: false,
      deltaX1: 0,
      deltaX2: 0,
      scrollStep: 20,
      dropDownPosition: {
        top: 0,
        left: 0
      },
      scrollPosition: {
        left: 0,
        right: 0
      },
      scrollAlign: {
        dir: 'flex-start',
        snap: 'left'
      },
      formValidate: {
        minLen: false
      },
      tabsModel: this.tabs,
      draggableOptions: {
        animation: 500,
        preventOnFilter: false,
        draggable: '.c-widget-sortable-tabs-bar__drag-tab',
        filter: '.c-widget-sortable-tabs-bar__drag-tab-input'
      }
    };
  },
  methods: {
    onTabRename() {
      this.currentTab.editable = true;
      this.closeDropDowns();
      this.isOverflown();
    },
    onTabRemove() {
      this.removeTab();
      this.resetTabState();
      this.closeDropDowns();
      this.isOverflown();
    },
    onInputChange(value, tab) {
      this.validateInputValue(value);
      this.tabsModel.map(item => {
        if (item.id === tab.id) {
          item.name = value;
        }
        return item;
      });
      this.$emit('rename', tab);
    },
    onTabViewChange(tab) {
      if (tab.editable === true && !this.formValidate.minLen) {
        tab.editable = false;
      }
    },
    onTabDragEnd() {
      this.tabsModel.map((item, index) => {
        item.id = index;
        return item;
      });
      this.closeDropDowns();
      this.$emit('dragend', this.tabsModel);
    },
    onTabAdd(tab) {
      const newTab = {
        name: tab.name,
        editable: false,
        id: this.tabsModel.length,
        active: false
      };
      this.tabsModel.push(newTab);
      this.closeDropDowns();
      this.isOverflown();
      this.$emit('add', newTab);
    },
    onResize() {
      this.closeDropDowns();
      this.isOverflown();
    },
    onStepLeft() {
      this.calculateDeltas();
      if (this.deltaX1 <= 0) {
        let step = this.getScrollStep(this.deltaX1);
        if (this.scrollAlign.snap === 'left') {
          this.scrollContentByStep(step);
        } else {
          this.scrollContentByStep(step * -1);
        }
      }
    },
    onStepRight() {
      this.calculateDeltas();
      if (this.deltaX2 <= 0) {
        let step = this.getScrollStep(this.deltaX2);
        if (this.scrollAlign.snap === 'right') {
          this.scrollContentByStep(step);
        } else {
          this.scrollContentByStep(step * -1);
        }
      }
    },
    getPositionData(evt) {
      const parentDims = this.$parent.$el.getBoundingClientRect();
      const targetDims = evt.target.getBoundingClientRect();
      const scrollOffset = this.getScrollOffset();
      const top = targetDims.top - parentDims.top;
      const left = targetDims.left - parentDims.left;
      const offsetY = targetDims.height + scrollOffset.y;
      const offsetX = evt.clientX - targetDims.left + scrollOffset.x;

      return {
        top,
        left,
        offsetX,
        offsetY
      };
    },
    getScrollOffset() {
      const offset = {
        x: -1,
        y: -1
      };
      if (window.pageXOffset !== null) {
        offset.x = window.pageXOffset;
        offset.y = window.pageYOffset;
      } else if (window.document.compatMode == 'CSS1Compat') {
        offset.x = window.document.documentElement.scrollLeft;
        offset.y = window.document.documentElement.scrollTop;
      } else {
        (offset.x = window.document.body.scrollLeft), (offset.y = window.document.body.scrollTop);
      }
      return offset;
    },
    getScrollStep(delta) {
      return delta % this.scrollStep === delta ? Math.abs(delta) : this.scrollStep;
    },
    showEditDropDown(evt, tab) {
      this.currentTab = tab;
      this.addDropDownActive = false;
      this.editDropDownActive = true;
      this.$nextTick(() => {
        const { top, left, offsetX, offsetY } = this.getPositionData(evt);
        this.setDropDownPosition(top, left, offsetX, offsetY);
        this.$refs['edit-dropdown'].show(true);
      });
    },
    showAddDropDown(evt) {
      this.currentTab = null;
      this.editDropDownActive = false;
      this.addDropDownActive = true;
      this.$nextTick(() => {
        const { top, left, offsetY } = this.getPositionData(evt);
        this.setDropDownPosition(top, left, 0, offsetY);
        this.$refs['add-dropdown'].show(true);
      });
    },
    setDropDownPosition(top, left, offsetX, offsetY) {
      this.dropDownPosition.top = top + offsetY;
      this.dropDownPosition.left = left + offsetX;
    },
    closeDropDowns() {
      this.addDropDownActive = false;
      this.editDropDownActive = false;
    },
    calculateDeltas() {
      this.deltaX1 = this.dragAreaElement.offsetLeft - this.dragAreaElement.parentNode.offsetLeft;
      this.deltaX2 =
        this.dragAreaElement.parentNode.clientWidth + Math.abs(this.deltaX1) - this.dragAreaElement.clientWidth;
    },
    scrollContentByStep(step) {
      this.scrollPosition[this.scrollAlign.snap] = this.scrollPosition[this.scrollAlign.snap] + step;
    },
    validateInputValue(value) {
      let trimValue = value.trim();
      if (trimValue === '' || trimValue === ' ' || trimValue.length === 0) {
        this.formValidate.minLen = true;
      } else {
        this.formValidate.minLen = false;
      }
      return trimValue;
    },
    isOverflown() {
      this.$nextTick(() => {
        this.calculateDeltas();
        if (this.deltaX1 >= 0) {
          if (this.deltaX2 <= 0) {
            this.scrollContentActive = true;
          } else if (this.deltaX2 > 0) {
            this.scrollContentActive = false;
          }
          this.scrollAlign.snap = 'left';
          this.scrollAlign.dir = 'flex-start';
          this.scrollPosition[this.scrollAlign.snap] = 0;
        } else if (this.deltaX1 < 0) {
          if (this.deltaX2 >= 0) {
            this.scrollAlign.snap = 'right';
            this.scrollAlign.dir = 'flex-end';
            this.scrollPosition[this.scrollAlign.snap] = 0;
          }
        }
      });
    },
    removeTab() {
      let filterResult = this.tabsModel
        .filter(item => item.id !== this.currentTab.id)
        .map((item, index) => {
          item.id = index;
          return item;
        });
      this.tabsModel = filterResult;
      this.$emit('remove', Object.assign({}, this.currentTab));
    },
    resetTabState() {
      if (this.currentTab.active === true) {
        this.setTabStateByIndex(0);
        this.currentTab = this.tabsModel[0];
        this.$emit('select', Object.assign({}, this.currentTab));
      } else {
        this.currentTab = null;
      }
    },
    setTabStateByIndex(index) {
      this.tabsModel.map(item => {
        if (item.id === index) {
          item.active = true;
        } else {
          item.active = false;
        }
        return item;
      });
    },
    reorderByIndex(tabs) {
      return [...tabs].sort((a, b) => a.id - b.id);
    },
    loadComponentsByTab(tab) {
      this.setTabStateByIndex(tab.id);
      this.closeDropDowns();
      this.$emit('select', Object.assign({}, tab));
    }
  },
  mounted() {
    this.dragAreaElement = this.$refs['dragarea'].$el;
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-widget-sortable-tabs-bar';
</style>
